import { Link } from "react-router-dom";
import { ArrowRight, Check, Path, MapPin, Stack, FileCsv, Hourglass } from "@phosphor-icons/react";
import { MarketingFrame } from "../components/MarketingFrame";

const SHIPPED = [
  "Import drone JPEGs from the SD card straight into a named session — sequence-named and session-tagged like every other capture",
  "Embedded DJI GPS (lat / lon / altitude / image direction) is read and preserved, not stripped",
  "Flight-log import: DJI .SRT subtitles and Airdata / Litchi CSV exports",
  "Photos without GPS get the nearest flight-log point by capture time (adjustable clock offset + tolerance)",
  "GPS, altitude, bearing and GPS timestamp are written into each JPEG's EXIF GPS IFD",
  "Every photo gets a flight-segment index (split on gaps in the log); the gallery filters by segment",
  "GeoJSON export: one Point per photo (with altitude, bearing, segment) plus the flight path as a LineString, bundled into the ZIP",
  "Windows Desktop Watcher: watches a folder or SD card, uploads JPG / DNG automatically and resumes interrupted uploads; GPS photos go to AERIAL-<date>, others to TABLETOP-<date>",
  "Personal, revocable upload keys and a one-command smoke test (PASS / FAIL in under a minute)",
  "Survey view per session: dark map with flight path and numbered photo pins, survey boundary with estimated acreage, altitude chart (average / max)",
  "KML (Google Earth) and GeoJSON downloads with flight path, photo points and boundary — also included in the ZIP",
];

const ROADMAP = [
  "GIS vector overlays (property lines, inspection zones, custom layers) on the survey map",
  "One-page PDF client inspection report with map snapshot, acreage and photo grid",
  "Watcher as a Windows service that starts with the PC, with watcher status in the app",
  "macOS watcher",
];

const STEPS = [
  { icon: Stack, t: "NAME THE JOB", d: "Create a session: site or asset ID, tags like roof / façade / pre-storm." },
  { icon: FileCsv, t: "IMPORT PHOTOS + LOG", d: "Gallery → Import SD / Drone (JPEGs), then Flight Log → Import (.SRT or .CSV)." },
  { icon: MapPin, t: "APPLY", d: "Match by capture time. GPS, altitude and bearing are written into the files; segments appear as filters." },
  { icon: Path, t: "EXPORT", d: "ZIP of renamed, tagged, GPS-baked JPEGs + photos.geojson for QGIS / ArcGIS / Google Earth." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Drone Capture Workflow — AIImageSuites",
  url: "https://aiimagesuites.pro/workflows/drone",
  description: "Import drone photos and DJI/Airdata flight logs into a named session; GPS, altitude, bearing and flight segment are written into each JPEG's EXIF and exported as GeoJSON.",
  mainEntity: {
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: "How does AIImageSuites handle drone photos?",
        acceptedAnswer: { "@type": "Answer", text: "Drone JPEGs are imported from the SD card into a named session, sequence-named and tagged. Existing DJI GPS EXIF is preserved; photos without GPS are matched to a DJI .SRT or Airdata/Litchi CSV flight log by capture time, and GPS, altitude and bearing are written into the EXIF GPS fields." } },
      { "@type": "Question", name: "Which flight-log formats are supported?",
        acceptedAnswer: { "@type": "Answer", text: "DJI .SRT subtitle logs (latitude/longitude/rel_alt/yaw lines, including the legacy GPS(lon,lat,alt) format) and CSV exports with latitude, longitude, altitude, heading and time columns, as produced by Airdata and Litchi." } },
      { "@type": "Question", name: "Does AIImageSuites export geospatial data?",
        acceptedAnswer: { "@type": "Answer", text: "Yes. Each session exports GeoJSON and KML (Google Earth) with one point per geotagged photo (filename, sequence, tags, altitude, bearing, segment), the flight path as a line, and the survey boundary polygon with its estimated area. GIS vector overlays and PDF inspection reports are on the roadmap and not yet available." } },
      { "@type": "Question", name: "Can drone photos upload automatically from my PC?",
        acceptedAnswer: { "@type": "Answer", text: "Yes, on Windows 10/11. The Desktop Watcher PowerShell script watches a folder or SD card, waits until each file finishes copying, uploads JPG and DNG files with resumable chunked uploads using a personal revocable upload key, and renames synced files SYNCED_ so they are never uploaded twice." } },
    ],
  },
};

export default function DroneWorkflowPage() {
  return (
    <MarketingFrame testid="drone-page" title="Drone Capture Workflow — GPS-baked, segment-grouped drone photos | AIImageSuites"
      description="Import drone JPEGs and DJI / Airdata flight logs into a named session. GPS, altitude, bearing and flight segment written into EXIF; GeoJSON export." jsonLd={JSONLD}>
      <article className="px-6 py-14 max-w-5xl mx-auto w-full space-y-12">
        <header className="space-y-4">
          <p className="label-mono text-neutral-500">WORKFLOW · DRONE CAPTURE</p>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95]" data-testid="drone-headline">
            DRONE PHOTOS THAT LAND<br /><span className="text-[#FF2A2A]">NAMED, TAGGED AND GEOTAGGED.</span>
          </h1>
          <p className="font-mono text-neutral-700 max-w-3xl">
            Pull the SD card, import into the job's session, drop in the flight log. Every frame leaves with the site name,
            sequence, tags, GPS, altitude and bearing inside the file — and a GeoJSON sidecar for your GIS.
          </p>
          <Link to="/signin" className="btn-flat px-5 py-3 inline-flex items-center gap-2" data-testid="drone-cta">Start Capturing <ArrowRight size={16} weight="bold" /></Link>
        </header>

        <section data-testid="drone-steps">
          <p className="label-mono text-neutral-500 mb-4">THE WORKFLOW</p>
          <ol className="grid grid-cols-1 md:grid-cols-4 border-2 border-black divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
            {STEPS.map((s, i) => (
              <li key={i} className="p-5">
                <s.icon size={28} weight="bold" className={i === 0 ? "text-[#FF2A2A]" : i === 3 ? "text-[#002FA7]" : ""} />
                <h2 className="font-display font-bold tracking-tighter text-base mt-3">{String(i + 1).padStart(2, "0")} · {s.t}</h2>
                <p className="text-xs font-mono text-neutral-700 mt-2 leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-6">
          <div className="border-2 border-black p-6" data-testid="drone-shipped">
            <p className="font-display font-bold tracking-tighter text-lg">WHAT WORKS TODAY</p>
            <ul className="mt-3 space-y-2 text-sm font-mono text-neutral-700">
              {SHIPPED.map((t, i) => <li key={i} className="flex gap-2"><Check size={16} weight="bold" className="text-green-700 shrink-0 mt-0.5" /><span>{t}</span></li>)}
            </ul>
          </div>
          <div className="border-2 border-dashed border-neutral-400 p-6 bg-[#F8F9FA]" data-testid="drone-roadmap">
            <p className="font-display font-bold tracking-tighter text-lg flex items-center gap-2"><Hourglass size={18} weight="bold" /> ROADMAP — NOT YET AVAILABLE</p>
            <ul className="mt-3 space-y-2 text-sm font-mono text-neutral-600">
              {ROADMAP.map((t, i) => <li key={i} className="flex gap-2"><span className="shrink-0">○</span><span>{t}</span></li>)}
            </ul>
            <p className="label-mono text-[10px] text-neutral-500 mt-4">Want one of these first? <Link to="/contact" className="underline">Tell us</Link>.</p>
          </div>
        </section>

        <section className="border-2 border-black p-6 space-y-3" data-testid="drone-honest">
          <p className="font-display font-bold tracking-tighter text-lg">THE BOUNDARY, PLAINLY</p>
          <p className="text-sm font-mono text-neutral-700">
            A browser cannot fly your drone or read its SD card live. The drone captures; AIImageSuites takes over at import —
            in the browser, or automatically from a Windows folder with the Desktop Watcher. Acreage is an estimate from the outer
            boundary of photo locations (not survey-grade), and altitude is shown as recorded by the aircraft, not height above ground.
            If your drone already writes GPS into every JPEG (DJI does), the flight log is optional — it adds bearing, fills gaps
            for frames without a fix, and gives you flight segments.
          </p>
        </section>

        <footer className="border-t-2 border-black pt-8 flex flex-wrap gap-4 label-mono text-neutral-600">
          <Link to="/use-cases/inspection-photos" className="underline underline-offset-4 hover:text-black">INSPECTION PHOTOS →</Link>
          <Link to="/help/camera" className="underline underline-offset-4 hover:text-black">GROUND CAMERA COMPATIBILITY →</Link>
          <Link to="/enterprise" className="underline underline-offset-4 hover:text-black">ENTERPRISE →</Link>
          <Link to="/pricing" className="underline underline-offset-4 hover:text-black">PRICING →</Link>
        </footer>
      </article>
    </MarketingFrame>
  );
}
